import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserPlus, CheckCircle2, XCircle, Loader2, Building2, GraduationCap, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RegistroPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [invitacion, setInvitacion] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setValidatingToken(false);
      setTokenValid(false);
    }
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await axios.get(`${API}/registro/validar-token?token=${token}`);
      setTokenValid(true);
      setInvitacion(response.data);
      
      // Si la invitación tiene email predefinido, usarlo
      if (response.data.email_invitado) {
        setFormData(prev => ({ ...prev, email: response.data.email_invitado }));
      }
    } catch (error) {
      setTokenValid(false);
    } finally {
      setValidatingToken(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/registro/invitacion`, {
        token,
        nombre: formData.nombre,
        email: formData.email,
        password: formData.password
      });

      setSuccess(true);
      toast.success('¡Registro exitoso!');
      
      // Redirigir según el tipo de entidad
      setTimeout(() => {
        navigate(response.data.redirect);
      }, 2000);

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  const getEntityIcon = () => {
    if (!invitacion) return <UserPlus className="w-8 h-8 text-white" />;
    if (invitacion.entidad_tipo === 'academia') return <GraduationCap className="w-8 h-8 text-white" />;
    if (invitacion.entidad_tipo === 'club') return <Users className="w-8 h-8 text-white" />;
    return <Building2 className="w-8 h-8 text-white" />;
  };

  const getGradientClass = () => {
    if (!invitacion) return 'from-emerald-500 to-blue-600';
    if (invitacion.entidad_tipo === 'academia') return 'from-emerald-500 to-emerald-600';
    if (invitacion.entidad_tipo === 'club') return 'from-purple-500 to-purple-600';
    return 'from-blue-500 to-blue-600';
  };

  if (validatingToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="p-8 text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Validando invitación...</p>
        </Card>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-slate-50 flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Invitación no válida</h2>
          <p className="text-slate-600 mb-6">
            Esta invitación no es válida o ya fue utilizada. Por favor, contacta al administrador para solicitar una nueva invitación.
          </p>
          <Button onClick={() => navigate('/')}>
            Ir al inicio
          </Button>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">¡Registro Exitoso!</h2>
          <p className="text-slate-600 mb-4">
            Tu cuenta ha sido creada correctamente. Serás redirigido en un momento...
          </p>
          <Loader2 className="w-6 h-6 text-emerald-500 animate-spin mx-auto" />
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-blue-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="p-8 shadow-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className={`w-16 h-16 bg-gradient-to-r ${getGradientClass()} rounded-full flex items-center justify-center mx-auto mb-4`}>
              {getEntityIcon()}
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Completa tu Registro
            </h1>
            {invitacion && (
              <div className="space-y-1">
                <p className="text-slate-600 text-sm">
                  Has sido invitado a unirte a:
                </p>
                <p className="font-semibold text-slate-900">
                  {invitacion.entidad_nombre || (invitacion.entidad_tipo === 'academia' ? 'Academia' : 'Club')}
                </p>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                  invitacion.rol_asignado === 'integrante' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-amber-100 text-amber-800'
                }`}>
                  Rol: {invitacion.rol_asignado === 'integrante' ? 'Integrante' : 'Staff'}
                </span>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="nombre">Nombre Completo *</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                required
                placeholder="Juan Pérez"
                data-testid="registro-nombre"
              />
            </div>

            <div>
              <Label htmlFor="email">Correo Electrónico *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder="tu@email.com"
                disabled={!!invitacion?.email_invitado}
                data-testid="registro-email"
              />
              {invitacion?.email_invitado && (
                <p className="text-xs text-slate-500 mt-1">
                  Esta invitación está destinada a este email
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="password">Contraseña *</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                placeholder="••••••••"
                minLength={6}
                data-testid="registro-password"
              />
              <p className="text-xs text-slate-500 mt-1">Mínimo 6 caracteres</p>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirmar Contraseña *</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                placeholder="••••••••"
                data-testid="registro-confirm-password"
              />
            </div>

            <Button
              type="submit"
              className={`w-full bg-gradient-to-r ${getGradientClass()} hover:opacity-90`}
              disabled={loading}
              data-testid="registro-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creando cuenta...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Crear Cuenta
                </>
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center text-xs text-slate-500">
            <p>Al registrarte, aceptas nuestros términos y condiciones</p>
          </div>
        </Card>
      </div>
    </div>
  );
}
